<script lang="ts" setup>
  import { computed } from 'vue'

  import useOuraAuth from '#web-oura-tools/auth'

  import { usePermissionsQuery } from '#ourahub-intra/queries/permissions'

  import type { OuraUser, Permission } from '#web-oura-tools/types/user'

  const props = defineProps<{
    user: OuraUser
  }>()

  const ouraAuth = useOuraAuth()

  const permissionsQuery = usePermissionsQuery()

  const isReleaseEnv = import.meta.env.VITE_APP_ENV === 'release'

  const initials = computed(() => {
    const names = props.user.email.split('@')[0]!.split('.')

    return names
      .slice(0, 2)
      .map((n) => n[0]!.toUpperCase())
      .join('')
  })

  function togglePermissionOverride(permission: Permission) {
    ouraAuth.togglePermission(permission.normalizedKey)
  }
</script>

<template lang="html">
  <v-menu offset="8" location="bottom" transition="scale-transition" :close-on-content-click="false">
    <template #activator="{ props: menuProps }">
      <o-btn-text v-bind="menuProps" icon class="mr-2">
        <v-avatar border size="small" color="blue-grey" :text="initials" />
      </o-btn-text>
    </template>

    <v-list width="300" class="bg-primary">
      <v-list-subheader :title="props.user.email" />

      <v-list-item v-if="props.user.canBypassAccessRights" slim class="text-body-1" title="Bypass access rights">
        <template #append>
          <v-switch
            hide-details
            density="compact"
            :model-value="props.user.isBypassingAccessRights"
            :base-color="props.user.isBypassingAccessRights ? undefined : 'red'"
            @click.stop="ouraAuth.toggleAdminBypass()"
          />
        </template>
      </v-list-item>

      <v-divider />

      <v-list class="bg-primary" :lines="false">
        <v-list-subheader>My Groups</v-list-subheader>

        <template v-if="permissionsQuery.isLoading.value">
          <v-skeleton-loader v-for="n in 3" :key="n" type="list-item" />
        </template>

        <v-list-item
          v-else-if="permissionsQuery.error.value"
          append-icon="mdi-refresh"
          class="text-body-1 text-error"
          prepend-icon="mdi-alert-circle-outline"
          :subtitle="permissionsQuery.error.value.message"
          @click="permissionsQuery.refetch()"
        >
          <template #title>
            <div class="text-wrap">Failed to load permissions</div>
          </template>
        </v-list-item>

        <template v-else>
          <v-menu
            v-for="group in permissionsQuery.myGroups.value"
            :key="group.key"
            open-on-hover
            location="start"
            :open-delay="100"
            :close-delay="100"
            :close-on-content-click="false"
          >
            <template #activator="{ props: groupMenuProps }">
              <v-list-item
                v-bind="groupMenuProps"
                slim
                prepend-icon="mdi-menu-left"
                append-icon="mdi-account-group"
                class="text-no-wrap text-body-1"
                :title="group.name"
                :class="{ 'font-italic': group.isPseudo }"
              />
            </template>

            <v-card width="320" max-height="60vh">
              <v-list density="compact" class="bg-primary permissions-list" :lines="false">
                <template v-if="group.allow.length > 0">
                  <v-list-subheader>Granted permissions</v-list-subheader>

                  <v-list-item v-for="permission in group.allow" :key="permission.key" slim class="text-no-wrap py-0">
                    <template #title>
                      <v-tooltip location="top" :text="permission.description" :disabled="!permission.description">
                        <template #activator="{ props: tooltipProps }">
                          <span v-bind="tooltipProps">
                            <v-icon
                              v-if="(permissionsQuery.deniersByKey.value[permission.normalizedKey] ?? []).length > 0"
                              color="red"
                              class="mr-1"
                            >
                              mdi-cancel
                            </v-icon>
                            <v-icon
                              v-else-if="permission.isGranted || permission.isOverridden"
                              class="mr-1"
                              :color="permission.isGranted ? 'green' : 'red'"
                            >
                              mdi-check
                            </v-icon>
                            <v-icon v-else class="mr-1" color="warning">mdi-alert-circle-outline</v-icon>
                            <span
                              :class="{
                                'denied-permission':
                                  (permissionsQuery.deniersByKey.value[permission.normalizedKey] ?? []).length > 0,
                              }"
                            >
                              {{ permission.name.replace(/ Users$/, ' Access') }}
                            </span>
                          </span>
                        </template>
                      </v-tooltip>
                    </template>

                    <template
                      #subtitle
                      v-if="(permissionsQuery.deniersByKey.value[permission.normalizedKey] ?? []).length > 0"
                    >
                      <div
                        v-for="denierName in permissionsQuery.deniersByKey.value[permission.normalizedKey] ?? []"
                        :key="denierName"
                        class="ml-8 text-break"
                      >
                        Denied by {{ denierName }}
                      </div>
                    </template>

                    <template #append>
                      <v-switch
                        v-if="!isReleaseEnv"
                        hide-details
                        density="compact"
                        :model-value="permission.isGranted"
                        :disabled="!permission.isGrantedIgnoringOverrides"
                        :base-color="permission.isOverridden ? 'red' : undefined"
                        @click.stop="togglePermissionOverride(permission)"
                      />
                    </template>
                  </v-list-item>
                </template>

                <template v-if="group.deny.length > 0">
                  <v-list-subheader>Denied permissions</v-list-subheader>
                  <v-list-item v-for="permission in group.deny" :key="permission.key" slim class="text-no-wrap py-0">
                    <template #title>
                      <v-tooltip location="top" :text="permission.description" :disabled="!permission.description">
                        <template #activator="{ props: tooltipProps }">
                          <span v-bind="tooltipProps">
                            <v-icon color="red" class="mr-1">mdi-close</v-icon>
                            <span>{{ permission.name }}</span>
                          </span>
                        </template>
                      </v-tooltip>
                    </template>

                    <template
                      #subtitle
                      v-if="
                        (permissionsQuery.grantersByKey.value[permission.normalizedKey] ?? []).some(
                          (g) => g !== group.name,
                        )
                      "
                    >
                      <div
                        v-for="granterName in (
                          permissionsQuery.grantersByKey.value[permission.normalizedKey] ?? []
                        ).filter((g) => g !== group.name)"
                        :key="granterName"
                        class="ml-8"
                      >
                        Denies in {{ granterName }}
                      </div>
                    </template>
                  </v-list-item>
                </template>

                <v-list-item v-if="group.allow.length === 0 && group.deny.length === 0" slim disabled>
                  No permissions
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </template>
      </v-list>
    </v-list>
  </v-menu>
</template>

<style lang="scss" scoped>
  .denied-permission {
    text-decoration: line-through;
    text-decoration-color: rgb(var(--v-theme-red, 244, 67, 54));
    text-decoration-thickness: 2px;
  }

  .permissions-list :deep(.v-switch) {
    min-height: 0;

    .v-selection-control {
      min-height: 0;
    }
  }

  // Align the toggle with the permission title row
  .permissions-list :deep(.v-list-item__append) {
    align-self: flex-start;
  }
</style>
